import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft } from "lucide-react";
import { AnimatedStarburst } from "@/components/icons/AnimatedStarburst";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import {
  ErpSidebar,
  PANEL_WIDTH,
} from "@/components/layout/ErpSidebar";
import { ErpMobileDrawer } from "@/components/layout/ErpMobileDrawer";
import { ErpLayoutContext, type ErpLayoutContextValue } from "./ErpLayoutContext";
import { useOnboardingState } from "@/hooks/useOnboardingState";

const INTENT_LABELS: Record<string, string> = {
  chat: "ระบบรวมแชท",
  selling: "ระบบขายออนไลน์",
  pos: "ระบบ POS",
  crm: "ระบบจัดการลูกค้า",
};

const DESKTOP_BREAKPOINT = "(min-width: 768px)";

/** Known top-level list segments — anything deeper is a detail page */
const LIST_SEGMENTS = new Set([
  "inventory", "orders", "customers", "selling", "stock", "pos",
  "storefront", "crm", "hr", "settings", "menu", "dashboard",
  "marketplace", "channels", "rewards", "team", "help",
]);

function useIsDetailPage(): boolean {
  const { pathname } = useLocation();
  // Detail pages have 3+ meaningful segments: /inventory/item/ITEM-001
  // List pages have 1-2: /inventory, /inventory/item (list)
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length < 3) return false;
  // /doc/:doctype/:name is always a detail page
  if (segments[0] === "doc" && segments.length >= 3) return true;
  // /print/:doctype/:name, /print-studio/:doctype/:name
  if ((segments[0] === "print" || segments[0] === "print-studio") && segments.length >= 3) return true;
  // Standard pattern: /section/doctype/:name (3+ segments under a known section)
  if (LIST_SEGMENTS.has(segments[0]) && segments.length >= 3) return true;
  return false;
}

export function ErpLayout() {
  const isDesktop = useMediaQuery(DESKTOP_BREAKPOINT);
  const [userPreferredCollapsed, setUserPreferredCollapsed] = useState(false);
  const [collapseSources, setCollapseSources] = useState<Set<string>>(
    () => new Set(),
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [logoReplay, setLogoReplay] = useState(0);
  const toggleButtonRef = useRef<HTMLButtonElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const isDetailPage = useIsDetailPage();
  const { view, intent } = useOnboardingState();
  const onboardingLabel = view === "intent-onboarding" && intent ? INTENT_LABELS[intent] : null;

  // Periodically flash the starburst logo on detail pages
  const [logoFlash, setLogoFlash] = useState(false);
  useEffect(() => {
    if (isDesktop || !isDetailPage) { setLogoFlash(false); return; }
    // Every 10s, show the starburst for 3.5s (enough for fly-in + hold + fly-out)
    const interval = setInterval(() => {
      setLogoFlash(true);
      setTimeout(() => setLogoFlash(false), 3500);
    }, 10000);
    return () => clearInterval(interval);
  }, [isDesktop, isDetailPage]);

  const effectiveCollapsed =
    userPreferredCollapsed || collapseSources.size > 0;

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const requestCollapse = useCallback((source: string) => {
    setCollapseSources((prev) => {
      if (prev.has(source)) return prev;
      const next = new Set(prev);
      next.add(source);
      return next;
    });
  }, []);

  const releaseCollapse = useCallback((source: string) => {
    setCollapseSources((prev) => {
      if (!prev.has(source)) return prev;
      const next = new Set(prev);
      next.delete(source);
      return next;
    });
  }, []);

  const contextValue = useMemo<ErpLayoutContextValue>(
    () => ({ requestCollapse, releaseCollapse, isCollapsed: effectiveCollapsed, setMobileHeaderHidden: () => {}, mobileHeaderHidden: false }),
    [requestCollapse, releaseCollapse, effectiveCollapsed],
  );

  // AppShell's <main> already applies marginLeft: RAIL_WIDTH (72px) for the app rail.
  // We only need to add PANEL_WIDTH (250px) when the sidebar panel is expanded.
  const offset = effectiveCollapsed ? 0 : PANEL_WIDTH;

  const content = isDesktop ? (
    <>
      <ErpSidebar
        collapsed={effectiveCollapsed}
        onToggleCollapse={() => setUserPreferredCollapsed((c) => !c)}
      />
      <div
        style={{
          marginLeft: `${offset}px`,
        }}
      >
        <Outlet />
      </div>
    </>
  ) : (
    <>
      <div className="sticky top-0 z-20 flex items-center h-12 px-3 bg-background border-b border-border/40">
        <button
          ref={toggleButtonRef}
          type="button"
          onClick={isDetailPage ? () => navigate(-1) : () => setDrawerOpen(true)}
          aria-expanded={isDetailPage ? undefined : drawerOpen}
          aria-controls={isDetailPage ? undefined : "erp-mobile-drawer"}
          aria-label={isDetailPage ? "Go back" : "Open menu"}
          className="relative flex items-center justify-center w-9 h-9 -ml-1 rounded-lg"
        >
          <AnimatePresence mode="wait" initial={false}>
            {isDetailPage && !logoFlash ? (
              <motion.div
                key="back"
                initial={{ opacity: 0, scale: 0.6, rotate: -90 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                exit={{ opacity: 0, scale: 0.6, rotate: 90 }}
                transition={{ duration: 0.2, ease: [0.32, 0.72, 0, 1] }}
              >
                <ChevronLeft className="h-6 w-6 text-foreground" />
              </motion.div>
            ) : (
              <motion.div
                key="logo"
                initial={{ opacity: 0, scale: 0.6, rotate: 90 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                exit={{ opacity: 0, scale: 0.6, rotate: -90 }}
                transition={{ duration: 0.2, ease: [0.32, 0.72, 0, 1] }}
              >
                <AnimatedStarburst size={26} replayTrigger={logoReplay} />
              </motion.div>
            )}
          </AnimatePresence>
        </button>
        {onboardingLabel && (
          <span className="ml-1 text-sm font-semibold text-foreground truncate">
            {onboardingLabel}
          </span>
        )}
      </div>
      <ErpMobileDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)} onExitComplete={() => setLogoReplay((n) => n + 1)}
        toggleButtonRef={toggleButtonRef}
      />
      <Outlet />
    </>
  );

  return (
    <ErpLayoutContext.Provider value={contextValue}>
      {content}
    </ErpLayoutContext.Provider>
  );
}
